import { useEffect, useState } from 'react';
import { CreditCard, Plus, Trash2, Pencil, CheckCircle2, Star, Wifi, FlaskConical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const FIELD_LABELS = {
  merchant_id: 'Merchant ID', terminal_id: 'Terminal ID', api_key: 'API Key',
  api_secret: 'API Secret', base_url: 'API Base URL',
};

const blankForm = { provider: 'simulator', mode: 'simulator', name: '', branch_id: '', bank_account_id: '', is_default: false, enabled: true, currency: 'SAR' };

export default function PaymentTerminalsPage() {
  const [providers, setProviders] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [txns, setTxns] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, t, b, tx, ba] = await Promise.all([
        api.get('/payment-terminals/providers'),
        api.get('/payment-terminals'),
        api.get('/branches').catch(() => ({ data: [] })),
        api.get('/payment-terminals/transactions').catch(() => ({ data: { transactions: [] } })),
        api.get('/bank-accounts?active_only=true').catch(() => ({ data: [] })),
      ]);
      setProviders(p.data?.providers || []);
      setTerminals(t.data?.terminals || []);
      setBranches(b.data || []);
      setTxns(tx.data?.transactions || []);
      setBankAccounts(ba.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const providerSpec = providers.find(p => p.key === form.provider) || { fields: [], secret_fields: [], note: '' };
  const branchName = (id) => branches.find(b => b.id === id)?.name || 'All branches';
  const providerLabel = (key) => providers.find(p => p.key === key)?.label || key;

  const openNew = () => { setEditing(null); setForm(blankForm); setDialog(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      provider: t.provider, mode: t.mode || 'simulator', name: t.name || '',
      branch_id: t.branch_id || '', bank_account_id: t.bank_account_id || '', is_default: !!t.is_default, enabled: t.enabled !== false,
      currency: t.currency || 'SAR',
      merchant_id: t.merchant_id || '', terminal_id: t.terminal_id || '', base_url: t.base_url || '',
      api_key: '', api_secret: '',
    });
    setDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give the terminal a name'); return; }
    setSaving(true);
    try {
      const payload = { ...form, branch_id: form.branch_id || null };
      // never send empty secrets (keeps the stored one)
      ['api_key', 'api_secret'].forEach(k => { if (!payload[k]) delete payload[k]; });
      if (editing) await api.put(`/payment-terminals/${editing.id}`, payload);
      else await api.post('/payment-terminals', payload);
      toast.success(editing ? 'Terminal updated' : 'Terminal added');
      setDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete terminal "${t.name}"?`)) return;
    try { await api.delete(`/payment-terminals/${t.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const runTest = async (t) => {
    setTestingId(t.id);
    try {
      const { data } = await api.post(`/payment-terminals/${t.id}/test`);
      if (data.ok) toast.success(`Test sale approved (${data.result?.card_brand || ''} ${data.result?.masked_pan || ''})`);
      else toast.warning('Test ran but was declined — try again');
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setTestingId(null); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="payment-terminals-page">
        <SettingsPageHeader
          icon={CreditCard}
          title="Payment Terminals"
          subtitle="Connect card terminals from any provider. Configure per branch, multiple terminals allowed."
          accent="indigo"
          actions={<Button onClick={openNew} data-testid="add-terminal-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Terminal</Button>}
        />

        {/* How-to-connect help */}
        <details className="rounded-xl border border-border bg-muted/30 p-4" data-testid="terminal-help">
          <summary className="cursor-pointer font-semibold text-sm text-foreground">How to connect a card terminal (Geidea &amp; others)</summary>
          <div className="mt-3 space-y-3 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground">Quick start (test with no hardware)</p>
              <p>Add a terminal with provider <b>Simulator</b> and mark it <b>Default</b>. In the POS, tap <b>Bank/Card → Card</b> and complete the bill — you'll see Connecting → Processing → Approved. Great for training before real hardware arrives.</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Connect Geidea</p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>From your <b>Geidea Merchant Portal</b> (or your Geidea account manager) get: <b>Merchant ID</b>, <b>Terminal ID</b>, <b>API Key</b>, and the <b>API Base URL</b>. (Geidea has no "username" — these 4 values are what you need.)</li>
                <li>Click <b>Add Terminal</b> → choose provider <b>Geidea</b> → pick the <b>branch</b> → paste the 4 values → name it (e.g. "Front Counter").</li>
                <li>Turn on <b>Live mode</b> and Save. Use <b>Test</b> to verify the config.</li>
                <li>In the POS, the <b>Card</b> button now sends the bill to this terminal automatically.</li>
              </ol>
            </div>
            <div>
              <p className="font-medium text-foreground">Other providers</p>
              <p>The same steps work for <b>Foodics Pay, HyperPay, Network International, MyFatoorah</b> or a <b>Custom</b> terminal — just pick the provider and fill the fields it asks for. You can add <b>multiple terminals per branch</b> and mark one as Default.</p>
            </div>
            <p className="text-xs italic">Note: Live charging for each provider is activated on request once credentials are saved — until then terminals run in Simulator mode so the flow is fully testable.</p>
          </div>
        </details>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : terminals.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-14 text-center text-muted-foreground">
            <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No terminals yet. Click <b>Add Terminal</b> to connect your first one (start with the Simulator to try the flow).
          </CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {terminals.map(t => (
              <Card key={t.id} className="border-border" data-testid={`terminal-card-${t.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">{t.name}</span>
                        {t.is_default && <span className="text-amber-500" title="Default"><Star className="w-4 h-4 fill-amber-400" /></span>}
                      </div>
                      <p className="text-xs text-muted-foreground">{providerLabel(t.provider)} · {branchName(t.branch_id)}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded-full font-medium inline-flex items-center gap-1 ${t.mode === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {t.mode === 'live' ? <Wifi className="w-3 h-3" /> : <FlaskConical className="w-3 h-3" />}
                      {t.mode === 'live' ? 'Live' : 'Simulator'}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground grid grid-cols-2 gap-1">
                    {t.terminal_id && <span>Terminal: <span className="font-mono">{t.terminal_id}</span></span>}
                    {t.merchant_id && <span>Merchant: <span className="font-mono">{t.merchant_id}</span></span>}
                    <span>Currency: {t.currency || 'SAR'}</span>
                    <span>{t.enabled === false ? 'Disabled' : 'Enabled'}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => runTest(t)} disabled={testingId === t.id} data-testid={`test-terminal-${t.id}`}>
                      {testingId === t.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Test
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-terminal-${t.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive ml-auto" onClick={() => remove(t)} data-testid={`delete-terminal-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Card-terminal transactions (reconciliation) */}
        {txns.length > 0 && (
          <Card className="border-border" data-testid="terminal-transactions-card">
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-border font-semibold text-sm">Card Terminal Transactions</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Date</th>
                      <th className="text-left px-4 py-2 font-medium">Provider</th>
                      <th className="text-left px-4 py-2 font-medium">Terminal</th>
                      <th className="text-left px-4 py-2 font-medium">Approval</th>
                      <th className="text-left px-4 py-2 font-medium">Card</th>
                      <th className="text-right px-4 py-2 font-medium">Amount</th>
                      <th className="text-left px-4 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map(tx => (
                      <tr key={tx.id} className="border-b border-border/50" data-testid={`txn-row-${tx.id}`}>
                        <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}</td>
                        <td className="px-4 py-2 capitalize">{providerLabel(tx.provider)}{tx.type === 'refund' ? ' (refund)' : ''}</td>
                        <td className="px-4 py-2 font-mono text-xs">{tx.terminal_id || '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs">{tx.auth_code || '—'}<span className="block text-[10px] text-muted-foreground">{tx.provider_tx_id}</span></td>
                        <td className="px-4 py-2 text-xs">{tx.card_brand} {tx.masked_pan}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{tx.currency} {Number(tx.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${tx.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : tx.status === 'declined' ? 'bg-red-50 text-red-700' : 'bg-stone-100 text-stone-600'}`}>{tx.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="terminal-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit Terminal' : 'Add Terminal'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Provider</Label>
                  <select value={form.provider} onChange={(e) => setForm(f => ({ ...f, provider: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="terminal-provider-select">
                    {providers.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Branch</Label>
                  <select value={form.branch_id} onChange={(e) => setForm(f => ({ ...f, branch_id: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="terminal-branch-select">
                    <option value="">All branches</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Settles into bank account</Label>
                <select value={form.bank_account_id || ''} onChange={(e) => setForm(f => ({ ...f, bank_account_id: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="terminal-bank-account-select">
                  <option value="">Auto (use branch/date fallback)</option>
                  {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.name}{a.bank_name ? ` · ${a.bank_name}` : ''}</option>)}
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">Card/bank sales on this terminal are attributed to this account — so two terminals in one branch can settle to different banks.</p>
              </div>
              {providerSpec.note && <p className="text-[11px] text-muted-foreground bg-muted/40 rounded-md p-2">{providerSpec.note}</p>}
              <div>
                <Label className="text-xs">Display name</Label>
                <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Front Counter" className="mt-1" data-testid="terminal-name-input" />
              </div>

              {(providerSpec.fields || []).map(field => (
                <div key={field}>
                  <Label className="text-xs">{FIELD_LABELS[field] || field}</Label>
                  <Input
                    type={providerSpec.secret_fields?.includes(field) ? 'password' : 'text'}
                    value={form[field] || ''}
                    onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={editing && providerSpec.secret_fields?.includes(field) ? '•••••• (leave blank to keep)' : ''}
                    className="mt-1" data-testid={`terminal-field-${field}`}
                  />
                </div>
              ))}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Currency</Label>
                  <Input value={form.currency} onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))} className="mt-1" data-testid="terminal-currency-input" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch checked={form.mode === 'live'} onCheckedChange={(v) => setForm(f => ({ ...f, mode: v ? 'live' : 'simulator' }))} data-testid="terminal-mode-toggle" disabled={form.provider === 'simulator'} />
                  <span className="text-sm">{form.mode === 'live' ? 'Live mode' : 'Simulator'}</span>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={form.is_default} onCheckedChange={(v) => setForm(f => ({ ...f, is_default: v }))} data-testid="terminal-default-toggle" /> Default for branch
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={form.enabled} onCheckedChange={(v) => setForm(f => ({ ...f, enabled: v }))} data-testid="terminal-enabled-toggle" /> Enabled
                </label>
              </div>
              {form.mode === 'live' && form.provider !== 'simulator' && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Live mode saves your credentials. The live terminal link for {providerLabel(form.provider)} is activated on request — until then use Simulator to test the flow.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="terminal-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}{editing ? 'Save' : 'Add Terminal'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
